import { assetCreateAttemptStorageKey } from './assetCreateAttemptStorage';

const PREVIEW_STORAGE_SUFFIX = '.imagePreviewSrcs.v1';

export function assetCreateImagePreviewStorageKey(loginId?: string | null): string {
  return `${assetCreateAttemptStorageKey(loginId)}${PREVIEW_STORAGE_SUFFIX}`;
}

/** Whether a stored `images[]` path can render without a picked {@link File} or saved preview. */
export function isResolvableCreateImageWirePath(path: string): boolean {
  const t = path.trim();
  if (!t) {
    return false;
  }
  return t.startsWith('data:') || /^https?:\/\//i.test(t) || t.startsWith('/');
}

function isPersistablePreviewSrc(value: unknown): value is string {
  return typeof value === 'string' && value.trim().startsWith('data:image/');
}

/** Device-local thumbnail data URLs parallel to create-form `images[]`. */
export function loadPersistedAssetCreateImagePreviews(
  loginId?: string | null,
): (string | undefined)[] | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(assetCreateImagePreviewStorageKey(loginId));
    if (!raw?.trim()) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return null;
    }
    return parsed.map((item) => (isPersistablePreviewSrc(item) ? item : undefined));
  } catch {
    return null;
  }
}

export function persistAssetCreateImagePreviews(
  previewSrcs: readonly (string | undefined)[],
  loginId?: string | null,
): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  const serializable = previewSrcs.map((src) =>
    isPersistablePreviewSrc(src) ? src : null,
  );
  if (!serializable.some(Boolean)) {
    clearPersistedAssetCreateImagePreviews(loginId);
    return;
  }
  try {
    localStorage.setItem(
      assetCreateImagePreviewStorageKey(loginId),
      JSON.stringify(serializable),
    );
  } catch {
    /* quota exceeded — previews are optional UX sugar */
  }
}

export function clearPersistedAssetCreateImagePreviews(loginId?: string | null): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(assetCreateImagePreviewStorageKey(loginId));
  } catch {
    /* ignore */
  }
}

/**
 * Restored drafts keep bare filenames in `images[]` but not the picked {@link File} bytes.
 * Drop those entries unless we have a matching persisted data-URL preview.
 */
export function reconcileDraftCreateImages(
  paths: readonly string[],
  previewSrcs: readonly (string | undefined)[] | null,
): { paths: string[]; previewSrcs: (string | undefined)[] } {
  const alignedPreviews =
    previewSrcs && previewSrcs.length === paths.length ? previewSrcs : [];

  const nextPaths: string[] = [];
  const nextPreviews: (string | undefined)[] = [];

  for (let i = 0; i < paths.length; i++) {
    const path = paths[i]?.trim() ?? '';
    if (!path) {
      continue;
    }
    const preview = alignedPreviews[i];
    if (isPersistablePreviewSrc(preview)) {
      nextPaths.push(path);
      nextPreviews.push(preview);
      continue;
    }
    if (isResolvableCreateImageWirePath(path)) {
      nextPaths.push(path);
      nextPreviews.push(undefined);
    }
  }

  return { paths: nextPaths, previewSrcs: nextPreviews };
}
